import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { transcriptAPI } from "../../lib/api";
import type { Transcript, TranscriptSegment } from "../../types/api";
import styles from "./TranscriptViewer.module.css";

interface TranscriptViewerProps {
  organizationId: string;
  podcastId: string;
  episodeId: string;
  /** Current audio playback time in seconds (for highlight sync) */
  currentTime?: number;
  /** Called when user clicks a timestamp to seek */
  onSeek?: (time: number) => void;
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightMatches(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const escaped = escapeRegExp(query);
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);
  // split() with a capture group alternates: non-match, match, non-match, …
  // Odd-indexed parts are the captured matches.
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className={styles.searchMatch}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export default function TranscriptViewer({
  organizationId,
  podcastId,
  episodeId,
  currentTime,
  onSeek,
}: TranscriptViewerProps) {
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editedSegments, setEditedSegments] = useState<TranscriptSegment[]>([]);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const segmentListRef = useRef<HTMLDivElement>(null);
  const activeSegmentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load transcript
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    transcriptAPI
      .get(organizationId, podcastId, episodeId)
      .then((res) => {
        if (!cancelled) {
          setTranscript(res.transcript);
          setEditedSegments(res.transcript.segments.map((s) => ({ ...s })));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.statusCode === 404) {
            setTranscript(null);
          } else {
            setError(err.message || "Failed to load transcript");
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, podcastId, episodeId]);

  // Find active segment based on currentTime
  const activeIndex = useMemo(() => {
    if (currentTime == null || !transcript) return -1;
    return transcript.segments.findIndex(
      (seg) => currentTime >= seg.start && currentTime < seg.end,
    );
  }, [currentTime, transcript]);

  // Auto-scroll to active segment
  useEffect(() => {
    if (activeIndex >= 0 && activeSegmentRef.current && !editing) {
      activeSegmentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex, editing]);

  // Search results
  const searchMatches = useMemo(() => {
    if (!searchQuery || !transcript) return new Set<number>();
    const q = searchQuery.toLowerCase();
    const matches = new Set<number>();
    transcript.segments.forEach((seg, i) => {
      if (seg.text.toLowerCase().includes(q)) matches.add(i);
    });
    return matches;
  }, [searchQuery, transcript]);

  // Focus search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const handleEditToggle = useCallback(() => {
    if (editing && hasChanges) {
      // Confirm discard
      if (!confirm("Discard unsaved changes?")) return;
    }
    if (!editing && transcript) {
      setEditedSegments(transcript.segments.map((s) => ({ ...s })));
      setHasChanges(false);
    }
    setEditing(!editing);
  }, [editing, hasChanges, transcript]);

  const handleSegmentTextChange = useCallback((index: number, newText: string) => {
    setEditedSegments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, text: newText };
      return updated;
    });
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!transcript) return;
    setSaving(true);
    try {
      const updated: Transcript = {
        ...transcript,
        segments: editedSegments,
      };
      await transcriptAPI.save(organizationId, podcastId, episodeId, updated);
      setTranscript(updated);
      setHasChanges(false);
      setEditing(false);
      toast.success("Transcript saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [transcript, editedSegments, organizationId, podcastId, episodeId]);

  const handleExport = useCallback(
    (format: "srt" | "vtt" | "txt") => {
      setShowExport(false);
      if (format === "txt" && transcript) {
        const text = transcript.segments
          .map((s) => (s.speaker ? `${s.speaker}: ${s.text}` : s.text))
          .join("\n\n");
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transcript.txt`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const url = transcriptAPI.exportUrl(episodeId, format as "srt" | "vtt");
        window.open(url, "_blank");
      }
    },
    [episodeId, transcript],
  );

  const segments = editing ? editedSegments : transcript?.segments || [];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading transcript…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!transcript) {
    return null; // No transcript available — don't render anything
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Transcript</h3>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={`${styles.iconButton} ${showSearch ? styles.iconButtonActive : ""}`}
            onClick={() => {
              setShowSearch(!showSearch);
              if (showSearch) setSearchQuery("");
            }}
            title="Search transcript"
          >
            🔍 Search
          </button>

          <button
            type="button"
            className={`${styles.iconButton} ${editing ? styles.iconButtonActive : ""}`}
            onClick={handleEditToggle}
            title={editing ? "Cancel editing" : "Edit transcript"}
          >
            ✏️ {editing ? "Cancel" : "Edit"}
          </button>

          <div className={styles.exportMenu}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowExport(!showExport)}
              title="Export transcript"
            >
              ⬇️ Export
            </button>
            {showExport && (
              <div className={styles.exportDropdown}>
                <button
                  type="button"
                  className={styles.exportOption}
                  onClick={() => handleExport("srt")}
                >
                  SubRip (.srt)
                </button>
                <button
                  type="button"
                  className={styles.exportOption}
                  onClick={() => handleExport("vtt")}
                >
                  WebVTT (.vtt)
                </button>
                <button
                  type="button"
                  className={styles.exportOption}
                  onClick={() => handleExport("txt")}
                >
                  Plain Text (.txt)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSearch && (
        <div className={styles.searchBar}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search transcript…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <span className={styles.searchCount}>
              {searchMatches.size} match{searchMatches.size !== 1 ? "es" : ""}
            </span>
          )}
        </div>
      )}

      <div className={styles.segmentList} ref={segmentListRef}>
        {segments.length === 0 ? (
          <div className={styles.empty}>No transcript segments found.</div>
        ) : (
          segments.map((segment, index) => {
            const isActive = index === activeIndex;
            const isMatch = searchMatches.has(index);
            return (
              <div
                key={index}
                ref={isActive ? activeSegmentRef : undefined}
                className={`${styles.segment} ${isActive ? styles.segmentActive : ""} ${isMatch ? styles.segmentHighlight : ""}`}
                onClick={() => onSeek?.(segment.start)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onSeek?.(segment.start);
                }}
                tabIndex={0}
              >
                <span className={styles.timestamp}>{formatTimestamp(segment.start)}</span>
                <div className={styles.segmentContent}>
                  {segment.speaker && <div className={styles.speaker}>{segment.speaker}</div>}
                  {editing ? (
                    <textarea
                      className={styles.textInput}
                      value={editedSegments[index]?.text || ""}
                      onChange={(e) => handleSegmentTextChange(index, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      rows={1}
                    />
                  ) : (
                    <div className={styles.text}>
                      {searchQuery ? highlightMatches(segment.text, searchQuery) : segment.text}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {editing && (
        <div className={styles.editBar}>
          <span className={styles.editBarInfo}>
            {hasChanges ? "Unsaved changes" : "No changes"}
          </span>
          <div className={styles.editBarActions}>
            <button type="button" className={styles.iconButton} onClick={handleEditToggle}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleSave}
              disabled={saving || !hasChanges}
              style={
                hasChanges
                  ? {
                      background: "var(--color-primary)",
                      color: "white",
                      borderColor: "var(--color-primary)",
                    }
                  : undefined
              }
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
